import { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import QuotationRoomsBoards from "app/pages/Booking/Quotation/QuotationRoomsBoards/QuotationRoomsBoards";
import {
	getAccommodationsWithFormattedBoards,
	getAllAvailableBoardsSortedByApiOrder,
} from "app/pages/Booking/Quotation/quotationSelectors";
import PropTypes from "prop-types";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import { HitType, useFlagship } from "@flagship.io/react-sdk";
import { FLAGSHIP_SCREENVIEW_KPIS } from "app/constants";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import withRouter from "app/utils/hocs/withRouter";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import QuotationBoardFilter from "app/pages/Booking/Quotation/Board/QuotationBoardFilter";
import { useFilterParam } from "app/features/product/productFilters/hooks/useFilterParam";
import { getHideDiscountHBX } from "app/reducers/brandSelector";

const QuotationRoomsBoardsContainer = props => {
	const {
		updateBooking,
		accommodations,
		selectedBoardCode,
		allAvailableBoards = [],
		hideDiscountHBX,
	} = props;

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });
	const [searchParams] = useSearchParams();
	// Board filter: URL persistence (e.g., ?boards=BB,AI)
	const { filters, setFilters, resetFilters } = useFilterParam(["boards"]);
	const boardFilterCodes = filters.boards || [];

	// Only show filter if there are multiple board options
	const shouldShowFilter = allAvailableBoards.length > 1;

	// Filter accommodations based on selected board codes
	const filteredAccommodations = useMemo(() => {
		if (boardFilterCodes.length === 0) {
			return accommodations;
		}

		return accommodations
			?.map(accommodation => {
				const filteredBoards = accommodation?.boards?.filter(board => {
					return boardFilterCodes.includes(board.boardCode);
				});

				if (filteredBoards?.length > 0) {
					return {
						...accommodation,
						boards: filteredBoards,
					};
				}
				return null;
			})
			.filter(Boolean);
	}, [accommodations, boardFilterCodes]);

	useEffect(() => {
		if (isElementOnScreen) {
			track("quote_view_room");
		}
	}, [isElementOnScreen]);

	// Auto-select board on initial load (from URL boardCode or first available)
	useEffect(() => {
		if (!accommodations?.length || selectedBoardCode) {
			return;
		}

		const firstAccommodation = accommodations[0];
		const urlBoardCode = searchParams.get("boardCode");

		const targetBoard = urlBoardCode
			? firstAccommodation?.boards?.find(
					b => b.code === urlBoardCode || b.boardCode === urlBoardCode
			  )
			: null;

		const boardToSelect = targetBoard || firstAccommodation?.boards?.[0];

		if (boardToSelect) {
			updateBooking({
				board: boardToSelect,
				accommodation: firstAccommodation,
			});
		}
	}, [accommodations, selectedBoardCode, updateBooking, searchParams]);

	// TODO déplacer l'envoi des ces hits dans QuotationContainer une fois que le composant sera en functional component
	const { sendHits } = useFlagship();
	const { pathname } = useLocation();
	const isSDP = pathname.includes("/sdp");

	useEffect(() => {
		sendHits([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: isSDP
					? FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_SDP_QUOTE
					: FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_PP_QUOTE,
			},
		]);
	}, [isSDP]);

	const onBoardSelection = useCallback((board, accommodation) => {
		updateBooking({
			board: board,
			accommodation: accommodation,
		});
	}, []);

	// Handle filter apply: update URL and auto-select cheapest board from first filtered accommodation
	const handleFilterChange = useCallback(
		codes => {
			setFilters({ boards: codes });

			if (codes.length > 0 && accommodations?.length > 0) {
				const filtered = accommodations
					.map(acc => {
						const matchingBoards = acc?.boards?.filter(b =>
							codes.includes(b.boardCode)
						);
						return matchingBoards?.length > 0
							? { ...acc, boards: matchingBoards }
							: null;
					})
					.filter(Boolean);

				const firstAccommodation = filtered[0];
				if (firstAccommodation) {
					const cheapestBoard = firstAccommodation.boards.reduce((cheapest, board) =>
						(board?.upgradePrice || 0) < (cheapest?.upgradePrice || 0)
							? board
							: cheapest
					);
					updateBooking({ board: cheapestBoard, accommodation: firstAccommodation });
				}
			}
		},
		[setFilters, accommodations, updateBooking]
	);

	return (
		<div className="quotation__item" ref={ref}>
			<FormattedMessage id="quotation.accommodation.and.board.title">
				{message => {
					return (
						<div className="quotation__headline">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL3}
								variantMD={TYPOGRAPHY_VARIANTS.XL4}
								isBold
								className="quotation__title"
								component="h2"
							>
								{message}
							</Typography>
						</div>
					);
				}}
			</FormattedMessage>
			{shouldShowFilter && (
				<QuotationBoardFilter
					hideDiscountHBX={hideDiscountHBX}
					availableBoards={allAvailableBoards}
					selectedBoardCodes={boardFilterCodes}
					onFilterChange={handleFilterChange}
					onReset={resetFilters}
				/>
			)}
			<div className="quotation__content">
				<QuotationRoomsBoards
					{...props}
					accommodations={filteredAccommodations}
					onBoardChange={onBoardSelection}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = state => {
	let booking = state.booking || {};

	const selectedAccommmodationCode = booking?.accommodation?.code;
	const selectedBoardCode = booking?.board?.code;

	return {
		selectedAccommmodationCode,
		selectedBoardCode,
		accommodations: getAccommodationsWithFormattedBoards(state),
		allAvailableBoards: getAllAvailableBoardsSortedByApiOrder(state),
		hideDiscountHBX: getHideDiscountHBX(state),
	};
};

QuotationRoomsBoardsContainer.propTypes = {
	selectedBoardCode: PropTypes.string,
	accommodations: PropTypes.array,
	allAvailableBoards: PropTypes.array,
	updateBooking: PropTypes.func,
	hideDiscountHBX: PropTypes.bool,
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
		},
		dispatch
	);
};

export default withRouter(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(QuotationRoomsBoardsContainer)
);
